import { addDays, getISODay } from 'date-fns'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { toNumber } from '@/lib/decimal'
import { formatHmm } from '@/lib/hours'
import type { DayTotal } from '@/lib/types'
import { cn } from '@/lib/utils'
import { isoWeekId, weekMetaFromId, type DayMeta } from '@/lib/week'
import { PeriodPicker } from './PeriodPicker'

interface DayNavProps {
  weekId: string
  dayIndex: number
  onChange: (weekId: string, dayIndex: number) => void
}

function dayIndexOf(date: Date): number {
  return getISODay(date) - 1
}

export function DayNav({ weekId, dayIndex, onChange }: DayNavProps) {
  const meta = weekMetaFromId(weekId)
  const day = meta.days[dayIndex]
  const today = new Date()
  const todayIso = meta.days.find((d) => d.date.toDateString() === today.toDateString())?.iso ?? null
  const isToday = day.iso === todayIso

  function step(delta: number) {
    const next = dayIndex + delta
    // addDays en niet 86.400.000 ms: op de dag dat de zomertijd eindigt duurt de
    // dag 25 uur, waardoor een vaste msec-stap op dezelfde dag blijft steken en
    // de navigatie een week terugspringt.
    if (next < 0) onChange(isoWeekId(addDays(meta.days[0].date, -1)), 6)
    else if (next > 6) onChange(isoWeekId(addDays(meta.days[6].date, 1)), 0)
    else onChange(weekId, next)
  }

  return (
    <>
      <div className="inline-flex items-center gap-1 rounded-lg border border-hairline bg-surface p-0.5">
        <button type="button" onClick={() => step(-1)} className="btn-ghost h-8 w-8 p-0" aria-label="Vorige dag">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <PeriodPicker
          mode="day"
          label={day.label.charAt(0).toUpperCase() + day.label.slice(1)}
          selected={day.date}
          onPick={(date) => onChange(isoWeekId(date), dayIndexOf(date))}
        />
        <button type="button" onClick={() => step(1)} className="btn-ghost h-8 w-8 p-0" aria-label="Volgende dag">
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <button
        type="button"
        onClick={() => onChange(isoWeekId(today), dayIndexOf(today))}
        disabled={isToday}
        title={isToday ? 'Je zit al op vandaag' : undefined}
        className="btn-ghost h-8 px-2.5 text-sm"
      >
        Vandaag
      </button>
    </>
  )
}

interface DayTotalsStripProps {
  weekId: string
  /** Alleen de dag en het getal; de dagsoort komt hier uit de kalender, niet uit de data. */
  totals: Pick<DayTotal, 'dayIndex' | 'total'>[]
  dayIndex: number
  onSelect: (dayIndex: number) => void
}

export function DayTotalsStrip({ weekId, totals, dayIndex, onSelect }: DayTotalsStripProps) {
  const days: DayMeta[] = weekMetaFromId(weekId).days
  const today = new Date()
  const todayIso = days.find((d) => d.date.toDateString() === today.toDateString())?.iso ?? null
  const byDay = new Map(totals.map((d) => [d.dayIndex, d]))
  const max = Math.max(8, ...totals.map((d) => toNumber(d.total)))

  return (
    <div className="flex h-8 items-end gap-1 pb-1.5 pt-1" role="group" aria-label="Uren per dag">
      {days.map((day) => {
        const info = byDay.get(day.dayIndex)
        const hours = toNumber(info?.total)
        const selected = day.dayIndex === dayIndex
        const isToday = day.iso === todayIso
        const height = hours > 0 ? Math.max(5, Math.round((hours / max) * 18)) : 3
        const label = `${day.short} ${day.date.getDate()}`
        return (
          <button
            key={day.dayIndex}
            type="button"
            onClick={() => onSelect(day.dayIndex)}
            title={`${label} · ${hours > 0 ? `${formatHmm(hours)} uur` : 'leeg'}`}
            aria-label={`${label}, ${hours > 0 ? `${formatHmm(hours)} uur` : 'leeg'}`}
            aria-pressed={selected}
            className="group flex h-full w-3 flex-col items-center justify-end rounded-xs hover:bg-fill-2"
          >
            <span
              style={{ height }}
              className={cn(
                'w-1.5 rounded-full transition-colors',
                selected ? 'bg-accent-600' : hours > 0 ? 'bg-ink-faint/50 group-hover:bg-ink-faint' : 'bg-fill-3',
                isToday && !selected && 'ring-1 ring-accent-600/40',
              )}
            />
          </button>
        )
      })}
    </div>
  )
}
